import { FsGroupFormGroup } from "./fund-strategy-export-types";

export class FundStrategyModel {
  constructor(
    public uuid?: string,
    public name: string = '',
    public totalCommitment: number = 0,
    public expectedExpenses: number = 0,
    public carryInterestPcnt: number = 0,
    public orgUUID: string = '',
    public groups: GroupFS[] = [],
    public lastSaveText: string = '',
    public lastUpdateDate?: Date,
    public createdDate?: Date,
    public createdBy: string = '',
  ) {
  }
}
export class GroupFS {
  constructor(
    public uuid?: string,
    public name: string = '',
    public grOrder: number = 0,
    public subGroups: SubGroupFS[] = [],
  ) {
  }
}

export class SubGroupFS {
  constructor(
    public uuid?: string,
    public name: string = '',
    public subGrOrder: number = 0,
    public roundsData: RoundsFS[] = [],
  ) {
  }
}

export class RoundsFS {
  constructor(
    public uuid?: string,
    public name: string = '',
    public roundOrder: number = 0,
    public companiesNumber: number = 0,
    public esop: number = 0,
    public preMoney: number = 0,
    public round: number = 0,
    public investment: number = 0,
    public writeOff: number = 0,
    public exitCompNumber: number = 0,
    public exitCompPrice: number = 0,
    // will not be saved in the database
    public holding?: number,
    public totalInvestment?: number,
    public proceeds?: number,
    public exitProceeds?: number,
    public exitMoic?: number,
    public postMoney?: number,
  ) {
  }
}

export class TotalsFS {
  constructor(
    public companiesNumber: number = 0,
    public writeOff: number = 0,
    public exitCompNumber: number = 0,
    public investment: number = 0, // total of total investments
    public proceed: number = 0,
    public gain: number = 0,
    public moic: number = 0,
  ) {
  }
}

export class TotalsCompaniesNumberFS {
  constructor(
    public companiesNumber: number = 0,
    public exitCompNumber: number = 0,
  ) {
  }
}

export class SaveModelEmitterDTO {
  groups: GroupFS[];
  message: string;
  grIndex: number;
  sbIndex: number;
  newOrder: number;
}

export class FsSettingsData {
  constructor(
    public totalCommitment: number = 0,
    public investment: number = 0,
    public expectedExpenses: number = 0,
    public expectedExpensesPcnt: number = 0,
    public unAllocatedCommitment: number = 0,
    public portfolioGain: number = 0,
    public netGain: number = 0,
    public carryInterest: number = 0,
    public carryInterestPcnt: number = 0,
    public netGainAfterCarry: number = 0,

    public moic: number = 0,
    public fundMultiple: number = 0,
    public tvpi: number = 0,
    public proceeds: number = 0,
  ) {
  }
}

export class FSFundMetricsDTO {
  constructor(
    public totalCommitment: number = 0,
    public expectedExpenses: number = 0,
    public carryInterestPcnt: number = 0,
    public investment: number = 0,
    public proceeds: number = 0,
  ) {}

  get portfolioGain(): number {
    return this.proceeds - this.investment;
  }

  get netGain(): number {
    return this.proceeds - this.investment - this.expectedExpenses;
  }

  get expectedExpensesPcnt(): number {
    return (this.expectedExpenses / this.totalCommitment) * 100;
  }

  get carryInterest(): number {
    const netGain = this.proceeds - this.investment - this.expectedExpenses;
    return netGain > 0 ? netGain * this.carryInterestPcnt / 100 : 0;
  }

  get unAllocatedCommitment(): number {
    return this.totalCommitment - this.expectedExpenses - this.investment;
  }

  get tvpi(): number {
    const minAllocation = Math.min(this.totalCommitment, this.investment + this.expectedExpenses);
    if (this.netGain > 0) {
      return minAllocation ? (this.netGain + minAllocation - this.carryInterest) / minAllocation : 0;
    }
    return minAllocation ? (this.netGain + minAllocation) / minAllocation : 0;
  }

  get netGainAfterCarry(): number {
    return this.proceeds - this.investment - this.expectedExpenses - this.carryInterest;
  }

  get moic(): number {
    return this.investment ? (this.proceeds / this.investment) : 0;
  }

  get fundMultiple(): number {
    const minAllocation = Math.min(this.totalCommitment, this.investment + this.expectedExpenses);
    return minAllocation ? (this.netGain + minAllocation) / minAllocation : 0;
  }
}

export class FSFundMetricsMiniDTO {
  constructor(
    public totalCommitment: number = 0,
    public expectedExpenses: number = 0,
    public carryInterestPcnt: number = 0,
  ) {}
}

export class FSPortfolioMetrics {
  constructor(
    public name: string = '',
    public companiesNumber: number = 0,
    public investment: number = 0,
    public proceeds: number = 0,
    public gain: number = 0,
    public moic: number = 0,
  ) {}

  updateMetrics(exitCompNumber: number, rInvestment: number, proceeds: number): void {
    this.companiesNumber += exitCompNumber;
    this.investment += rInvestment * exitCompNumber;
    this.proceeds += proceeds * exitCompNumber;
  }

  calculateGain(): void {
    this.gain = this.proceeds - this.investment;
  }

  calculateMoic(): void {
    this.moic = this.investment ? (this.proceeds / this.investment) : 0;
  }

}

export class AtLossFSPortfolioMetrics extends FSPortfolioMetrics {
  name = 'At loss';
}

export class AtGainFSPortfolioMetrics extends FSPortfolioMetrics {
  name = 'At gain';
}

export class WriteOffFSPortfolioMetrics extends FSPortfolioMetrics {
  name = 'Write-off';
}

export class TotalFSPortfolioMetrics extends FSPortfolioMetrics {
  name = 'Total';
}

export interface FundStrategyExportExcelData {
  groups: FsGroupFormGroup[];
  metrics: FsSettingsData;
}

export class FsAnalyticsCards {
  constructor(
    public initialInvestment: number = 0,
    public followOnInvestment: number = 0,
    public totalInvestment: number = 0,
    public initialRoundsNumber: number = 0,
    public followOnRoundsNumber: number = 0,
    public roundsNumber: number = 0,
    public avgInitialInv: number = 0,
    public initialCompaniesNumber: number = 0,
    public avgFollowOnInv: number = 0,
    public followOnCompaniesNumber: number = 0,
    public avgTotalInv: number = 0,
    public avgInitialHolding: number = 0,
    public avgExitHolding: number = 0,
    public holdingDilution: number = 0,
  ) {
  }
}

export class FsAggregatedAnalytics {
  constructor(
    public initialInvestment: number = 0,
    public followOnInvestment: number = 0,
    public totalInvestment: number = 0,
    public initialRoundsNumber: number = 0,
    public followOnRoundsNumber: number = 0,
    public initialCompaniesNumber: number = 0,
    public followOnCompaniesNumber: number = 0,
    public roundsNumber: number = 0,
    public initialHoldingData: FsAnalyticsHoldingData = new FsAnalyticsHoldingData(),
    public exitHoldingData: FsAnalyticsHoldingData = new FsAnalyticsHoldingData(),
  ) {}
}

export class FsAnalyticsHoldingData {
  constructor(
    public weightedHoldingSum: number = 0,
    public companiesNumber: number = 0,
  ) {}
}

export class FsAnalyticsData {
  constructor(
    public atGain: AtGainFSPortfolioMetrics = new AtGainFSPortfolioMetrics(),
    public atLoss: AtLossFSPortfolioMetrics = new AtLossFSPortfolioMetrics(),
    public writeOff: WriteOffFSPortfolioMetrics = new WriteOffFSPortfolioMetrics(),
    public total: TotalFSPortfolioMetrics = new TotalFSPortfolioMetrics(),
    public analyticsCards: FsAnalyticsCards = new FsAnalyticsCards()
  ) {}
}

export interface SubGroupTooltipAnalytics {
  exitCompanyPrice: number;
  exitCompNumber: number;
  proceeds: number;
  holding: number;
  moic: number;
}
